import { useEffect, useState } from 'react';
import { api, describeError, type SkillView, type UpgradeView } from './api.js';

/** Anthony's saved skills: workflows Alfred follows when a request matches. Made in chat. */
export function SkillsSettings() {
  const [skills, setSkills] = useState<SkillView[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = () =>
    api
      .skills()
      .then((r) => setSkills(r.skills))
      .catch(() => {});
  useEffect(() => void refresh(), []);

  const remove = async (id: string) => {
    setBusy(true);
    setMessage(null);
    try {
      await api.removeSkill(id);
      await refresh();
    } catch (err) {
      setMessage(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!skills) return null;
  return (
    <section>
      <h3>Saved skills</h3>
      <p className="muted small">
        Workflows Alfred remembers how to do. Say “save that as a skill” after something you’ll ask
        for again, or “when I say weekly recap, do…”. Correct him and he’ll offer to update it.
      </p>
      {skills.length === 0 && <p className="small">No skills yet.</p>}
      <ul className="routine-list">
        {skills.map((s) => (
          <li key={s.id} className="skill-item">
            <div>
              <button
                className="link-button"
                onClick={() => setOpen(open === s.id ? null : s.id)}
                aria-expanded={open === s.id}
              >
                <strong>{s.name}</strong>
              </button>
              <div className="muted small">
                {s.trigger}
                {s.project ? ` · ${s.project}` : ''} · used {s.uses}{' '}
                {s.uses === 1 ? 'time' : 'times'}
              </div>
              {open === s.id && <pre className="skill-steps small">{s.steps}</pre>}
            </div>
            <button className="btn btn-small" disabled={busy} onClick={() => void remove(s.id)}>
              Remove
            </button>
          </li>
        ))}
      </ul>
      {message && <p className="small">{message}</p>}
    </section>
  );
}

const UPGRADE_LABEL = { approved: '⏳ Waiting to be built', done: '✅ Built', dropped: 'Dropped' };

/** Changes to Alfred's own code: approved in the app, built in Claude Code on the PC. */
export function UpgradesSettings() {
  const [upgrades, setUpgrades] = useState<UpgradeView[] | null>(null);
  useEffect(
    () =>
      void api
        .upgrades()
        .then((r) => setUpgrades(r.upgrades))
        .catch(() => {}),
    [],
  );
  if (!upgrades) return null;
  return (
    <section>
      <h3>Upgrades to Alfred</h3>
      <p className="muted small">
        Ask Alfred to “propose an upgrade” for anything settings, memory or skills can’t do. He
        writes it up for you to approve; approved ones get built in Claude Code on your PC (say “do
        Alfred’s approved upgrades”).
      </p>
      {upgrades.length === 0 && <p className="small">None yet.</p>}
      <ul className="routine-list">
        {upgrades.map((u) => (
          <li key={u.id}>
            <div>
              <strong>{u.title}</strong>
              <div className="muted small">
                {UPGRADE_LABEL[u.status]}
                {u.note ? ` · ${u.note}` : ''}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
